import { useContext, useMemo } from 'react';
import AppliedFiltersContext from '~/context/srp/AppliedFiltersContext';
import ConditionalTileContext from '~/context/srp/ConditionalTileContext';
import DeviceContext, { useDeviceData } from '~/context/srp/DeviceContext';
import {
  useFinanceInfo,
  useHasSoftPull,
  useTradeInfo
} from '~/context/srp/FinanceAndTradeContext';
import SearchContext from '~/context/srp/SearchContext';
import type { ConditionalTile } from '~/contracts/srp';
import getConditionalTiles from '~/services/conditionalTiles';

function useHasEligibleDownPaymentRange() {
  const financeInfo = useFinanceInfo();

  return financeInfo
    ? financeInfo.minDownPayment >= 0 && financeInfo.minDownPayment <= 1000
    : false;
}

export default function useConditionalTiles(): ConditionalTile[] {
  const { appliedFilters, pageNumber } = useContext(AppliedFiltersContext);
  const { filteredByMinDown } = useContext(ConditionalTileContext);
  const { screenSize } = useContext(DeviceContext);
  const { showFinance } = useContext(SearchContext);
  const { browserName, deviceName } = useDeviceData();
  const hasSoftPull = useHasSoftPull();

  const hasEligibleDownPaymentRange = useHasEligibleDownPaymentRange();
  const tradeInfo = useTradeInfo();
  const hasActiveTrade = Object.keys(tradeInfo).length !== 0;

  const hasPositiveEquityIfActiveTrade =
    tradeInfo?.totalTradeInEquity == null || tradeInfo.totalTradeInEquity > 0;

  return useMemo(
    () =>
      getConditionalTiles({
        device: { browserName, deviceName, ...screenSize },
        filteredByMinDown,
        filters: appliedFilters,
        hasActiveTrade,
        hasEligibleDownPaymentRange,
        hasPositiveEquityIfActiveTrade,
        hasSoftPull,
        pageNumber,
        showFinance
      }),
    [
      appliedFilters,
      browserName,
      deviceName,
      filteredByMinDown,
      hasActiveTrade,
      hasEligibleDownPaymentRange,
      hasPositiveEquityIfActiveTrade,
      hasSoftPull,
      pageNumber,
      screenSize,
      showFinance
    ]
  );
}
